import { Query, QueryClient } from '@tanstack/react-query';

import { syncGuestAddressToBackend } from '../../addresses/services/syncGuestAddress';

/**
 * Запросы, ответ которых не зависит от аккаунта: меню, баннеры, филиалы,
 * геокодинг. Их при смене аккаунта не трогаем, чтобы главная не мигала
 * скелетонами. Всё остальное считается данными аккаунта — новый ключ, не
 * внесённый сюда, сбросится, и это безопасная сторона: лишний запрос лучше,
 * чем чужие данные.
 */
const PUBLIC_QUERY_KEYS = new Set([
  'categories',
  'product',
  'product-search',
  'banners',
  'branches',
  'branches-search',
  'pickup-branches',
  'search-addresses',
  'user-address',
  'delivery-availability',
  'nearest-branch',
]);

const isAccountQuery = (query: Query) =>
  !PUBLIC_QUERY_KEYS.has(String(query.queryKey[0]));

/**
 * Кэш запросов общий на всё приложение и не знает, чей он: история баллов,
 * заказы, уведомления, карты прошлого аккаунта оставались на экране и после
 * входа в другой. При входе сбрасываем данные аккаунта — активные запросы
 * сразу перезапросятся уже с новым токеном.
 */
export const resetAccountQueries = (queryClient: QueryClient) =>
  queryClient.resetQueries({ predicate: isAccountQuery });

/**
 * При выходе — только удаляем: токена уже нет, и перезапрос личных данных
 * ушёл бы в 401. Экраны после выхода перерисовываются гостем и новых
 * запросов не делают.
 */
export const removeAccountQueries = (queryClient: QueryClient) =>
  queryClient.removeQueries({ predicate: isAccountQuery });

/**
 * Всё, что происходит в момент появления токена.
 *
 * Токен `client/otp-auth` — тот же статический `clients.token` старого API:
 * он не истекает и не отзывается, поэтому храним его ровно так же, как раньше
 * хранил `signInOTP`, и перехватчик axios подставит его в заголовок `Token`.
 *
 * Вход и регистрация приводят к одному и тому же состоянию, поэтому шаги
 * живут здесь, а не дублируются в двух сервисах.
 */
export const startSession = (token: string, queryClient: QueryClient) => {
  localStorage.setItem('token', token);
  // Флаг ставит выход из аккаунта: пока он висит, useGetUserProfile
  // игнорирует пришедший профиль и пользователь остаётся гостем.
  localStorage.removeItem('loggedOut');

  // Профиль (`user-profile`) тоже в их числе — он перезапросится с новым токеном.
  resetAccountQueries(queryClient);
  syncGuestAddressToBackend(queryClient);
};
